import { useEffect } from "react";

// Post-translation corrections for Google Translate's French output.
// Google Translate produces a few phrasings the service prefers to override.
// This module runs ONLY when the page is translated to French and rewrites
// specific translated text nodes to the desired wording.
const isFrench = () => /googtrans=\/en\/fr(\b|;|$)/.test(document.cookie);

const CORRECTIONS = [
  // Navigation: "Home" -> GT "Maison" -> desired "Accueil"
  { test: (v) => v.trim() === "Maison", apply: () => "Accueil" },
  // Programs heading: "Care for..." -> GT "Prenez soin..." -> desired "Prendre soin..."
  { test: (v) => v.includes("Prenez soin"), apply: (v) => v.replace(/Prenez soin/g, "Prendre soin") },
  // Home NQS line: "...assessment on 27 May" -> GT "...la plus récente sur" -> "...la plus récente le"
  {
    test: (v) => v.includes("la plus récente sur"),
    apply: (v) => v.replace(/la plus récente sur/g, "la plus récente le\u00a0"),
  },
  // Brand name: GT mistranslates "Telopea Park OSHC" -> "Centre de santé pour enfants de
  // Telopea Park" (health centre for kids). Keep the brand / OSHC untranslated.
  {
    test: (v) => v.includes("Centre de santé pour enfants de Telopea Park"),
    apply: (v) => v.replace(/Centre de santé pour enfants de Telopea Park/g, "Telopea Park OSHC"),
  },
  // Home hero subtitle: "Care that wraps around the school day" -> GT "Des soins qui entourent
  // la journée scolaire" -> preferred "Une prise en charge qui entoure la journée scolaire".
  {
    test: (v) => v.includes("Des soins qui entourent la journée scolaire"),
    apply: (v) =>
      v.replace(
        /Des soins qui entourent la journée scolaire/g,
        "Une prise en charge qui entoure la journée scolaire",
      ),
  },
  // Programs BSC: children "walked to their school lines" -> GT "...jusqu'à leur école" ->
  // preferred "...jusqu'à leur classe".
  {
    test: (v) => v.includes("leur école"),
    apply: (v) => v.replace(/leur école/g, "leur classe"),
  },
  // Team (Educational Leader): GT adds subject "Elle guide..." -> preferred "Guide...".
  {
    test: (v) => v.includes("Elle guide le programme éducatif"),
    apply: (v) => v.replace(/Elle guide le programme éducatif/g, "Guide le programme éducatif"),
  },
  // NQS: "Meeting NQS" -> GT "Réunion NQS" (wrong sense) -> preferred "Répond aux NQS".
  {
    test: (v) => /R[ée]union NQS/.test(v),
    apply: (v) => v.replace(/R[ée]union NQS/g, "Répond aux NQS"),
  },
];

const correctNode = (node) => {
  const value = node.nodeValue;
  if (!value || !value.trim()) return;
  let out = value;
  CORRECTIONS.forEach((c) => {
    if (c.test(out)) out = c.apply(out);
  });
  if (out !== value) node.nodeValue = out;
};

const walk = (root) => {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    correctNode(node);
    node = walker.nextNode();
  }
};

const TranslationCorrections = () => {
  useEffect(() => {
    if (!isFrench()) return undefined;

    // Google Translate applies asynchronously, so sweep a few times after load.
    walk(document.body);
    const timers = [300, 900, 2000, 4000, 7000].map((t) => setTimeout(() => walk(document.body), t));

    // Keep correcting as Google Translate mutates / re-injects text nodes.
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.type === "characterData" && m.target) {
          correctNode(m.target);
        }
        if (m.addedNodes) {
          m.addedNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) correctNode(node);
            else if (node.nodeType === Node.ELEMENT_NODE) walk(node);
          });
        }
      });
    });
    observer.observe(document.body, { subtree: true, childList: true, characterData: true });

    return () => {
      timers.forEach(clearTimeout);
      observer.disconnect();
    };
  }, []);

  return null;
};

export default TranslationCorrections;
